import { Loader2 } from "lucide-react";
import { useI18n } from "../../i18n";
import { errorText, isServerFailure } from "../../lib/api";

export const PageHeader = ({ title, subtitle, actions, testId }) => (
  <header className="am-page-head" data-testid={testId || "page-header"}>
    <div className="min-w-0">
      <h1 className="am-h1">{title}</h1>
      {subtitle && <p className="am-sub">{subtitle}</p>}
    </div>
    {actions && <div className="am-page-actions">{actions}</div>}
  </header>
);

const TONE = { queued: "muted", auditing: "run", writing: "run", ready: "good", approved: "solid", skipped: "warn",
  failed: "bad", ai_failed: "bad", draft: "muted", starting: "run", processing: "run", completed: "good" };

export const StatusPill = ({ status, prefix = "pstatus", testId }) => {
  const { t } = useI18n();
  return (
    <span className={`am-pill pill-${TONE[status] || "muted"}`} data-testid={testId}>
      {TONE[status] === "run" && <Loader2 className="h-3 w-3 animate-spin" />}
      {t(`${prefix}.${prefix === "campaigns" ? `status_${status}` : status}`)}
    </span>
  );
};

export const Spinner = ({ label, error, onRetry }) => (error ? <FetchError error={error} onRetry={onRetry} /> : (
  <div className="am-loading" data-testid="loading-indicator"><Loader2 className="h-4 w-4 animate-spin" />{label}</div>
));

// Never a blank area: a failed load shows the reason and a Retry button.
export const FetchError = ({ error, onRetry }) => {
  const { t } = useI18n();
  return (
    <div className="am-notice m-4" role="alert" data-testid="load-network-error">
      <p className="m-0 flex-1">{isServerFailure(error) || !error?.response ? t("net.failed") : errorText(t, error)}</p>
      {onRetry && (isServerFailure(error) || !error?.response) && <button type="button" className="am-btn am-btn-primary am-btn-sm" onClick={() => onRetry()} data-testid="load-retry-button">{t("net.retry")}</button>}
    </div>
  );
};

// Load failure: real 4xx shows `notFound`; network/server errors show the translated retry instead.
export const LoadError = ({ error, onRetry, notFound }) => {
  if (error?.response && error.response.status < 500) return notFound;
  return <FetchError error={error} onRetry={onRetry} />;
};

export const Field = ({ label, hint, error, children, htmlFor }) => (
  <div className="am-field">
    {label && <label className="am-label" htmlFor={htmlFor}>{label}</label>}
    {children}
    {hint && !error && <p className="am-hint">{hint}</p>}
    {error && <p className="am-error" role="alert">{error}</p>}
  </div>
);

export const scoreTone = (v) => (v == null ? "none" : v < 5 ? "bad" : v < 8 ? "warn" : "good");

export const ScoreChip = ({ value, testId }) => {
  const { fmtNum } = useI18n();
  return value == null ? <span className="text-zinc-400">—</span>
    : <span className={`am-chip tone-${scoreTone(value)}`} data-testid={testId}>{fmtNum(value, 1)}</span>;
};
